import produce from 'immer';

import {assert} from 'Asserts';
import deepCopy from 'lib/deepCopy';
import {
  ProcedureEdgeType,
  ProcedureFlowchart,
  ProcedureMaterialInputSetting,
  ProcedureMaterialOutputSetting,
} from 'procedures/ProcedureDomainTypes';
import {BIOENTITY_LINK_FIELD_TYPE} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldConstants';
import {FieldType, isRequiredLinkTagSchema} from 'schemas/fields/SchemaFieldConstants';

import {getEdgeTypeFromHandleId, getMaterialCidFromHandle} from './handleIdentifierUtils';

/**
 * Given a node CID and a handle ID, find the matching material output setting associated with that
 * handle, if one exists on the given node. This is meant to be used when initiating an edge.
 */
export function findMaterialOutputSetting(
  flowchart: ProcedureFlowchart,
  methodCid: string,
  handleId: string
): ProcedureMaterialOutputSetting | null {
  const edgeType = getEdgeTypeFromHandleId(handleId);
  if (edgeType !== ProcedureEdgeType.MATERIAL_EDGE) {
    return null;
  }
  const materialCid = getMaterialCidFromHandle(handleId);
  const methodNode = flowchart.nodes.find((node) => node.cid === methodCid);
  assert(methodNode != null, 'Expected a valid method node', {flowchart, methodCid});
  return (
    methodNode.materialOutputSettings.find((setting) => setting.materialOutputVersion.cid === materialCid) ||
    null
  );
}

export function isMaterialConnectionCompatible(
  materialOutputSetting: ProcedureMaterialOutputSetting | null,
  materialInputSetting: ProcedureMaterialInputSetting
): boolean {
  // It's fine to invoke this function without an outputSetting
  if (materialOutputSetting == null) {
    return false;
  }

  const inputDataType = materialInputSetting.materialInputVersion.dataType;
  const outputDataType = materialOutputSetting.materialOutputVersion.dataType;

  // Output: specific schema -> Input: text
  if (inputDataType.type !== FieldType.BIOENTITY_LINK) {
    return false;
  }

  // Output: specific schema -> Input: specific schema
  if (
    isRequiredLinkTagSchema(inputDataType.requiredLink) &&
    isRequiredLinkTagSchema(outputDataType.requiredLink)
  ) {
    return inputDataType.requiredLink.tagSchema.id === outputDataType.requiredLink.tagSchema.id;
  }

  // Output: specific schema -> Input: any entity
  return true;
}

// We create a new, modified inputSetting which is compatible with the given output setting.
// In practice: the output materials TYPE is copied into the input material, and any planned
// values are removed. Note that this function does not _assess_ compatibility.
export function makeCompatibleMaterialInputSetting(
  materialOutputSetting: ProcedureMaterialOutputSetting,
  initialMaterialInputSetting: ProcedureMaterialInputSetting
): ProcedureMaterialInputSetting {
  return produce(initialMaterialInputSetting, (draftInputSetting) => {
    draftInputSetting.materialInputVersion.dataType = deepCopy(
      materialOutputSetting.materialOutputVersion.dataType
    );

    // If the input type was any entity, then it is already compatible and we will not modify the data type
    if (
      initialMaterialInputSetting.materialInputVersion.dataType.type === FieldType.BIOENTITY_LINK &&
      !isRequiredLinkTagSchema(initialMaterialInputSetting.materialInputVersion.dataType.requiredLink)
    ) {
      draftInputSetting.materialInputVersion.dataType = BIOENTITY_LINK_FIELD_TYPE;
    }

    // We remove any linked materials, without touching the associated amounts
    for (const value of draftInputSetting.values) {
      value.material = {
        type: FieldType.BIOENTITY_LINK,
        value: '',
        targetFiles: [],
      };
    }
  });
}
